import type { RxJsonSchema } from 'rxdb'
import type { SubscriptionDocument } from '@/lib/types/subscription'

/**
 * RxDB Schema for Subscription
 * One subscription per workspace (workspace-level billing like Linear)
 *
 * v1 (2026-05-07): adds dual-provider columns alongside the original Stripe
 * pair. `billingProvider` is the routing key — every consumer that resolves
 * a provider id should switch on this rather than null-coalesce, mirroring
 * the backend Subscription.ProviderSubscriptionId getter.
 */
export const subscriptionSchema: RxJsonSchema<SubscriptionDocument> = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    planId: {
      type: 'string',
      maxLength: 36,
    },
    status: {
      type: 'string',
      maxLength: 50,
      enum: ['trialing', 'active', 'past_due', 'canceled', 'expired'],
    },
    trialStartDate: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    trialEndDate: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    currentPeriodStart: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    currentPeriodEnd: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    billingInterval: {
      type: 'string',
      maxLength: 20,
      enum: ['monthly', 'annual'],
    },
    canceledAt: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    cancelAtPeriodEnd: {
      type: 'boolean',
      default: false,
    },
    stripeCustomerId: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    stripeSubscriptionId: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    paddleCustomerId: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    paddleSubscriptionId: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    billingProvider: {
      type: 'string',
      maxLength: 20,
      default: 'stripe',
    },
    billingEmail: {
      type: 'string',
      maxLength: 255,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

  },
  required: [
    'id',
    'envId',
    'planId',
    'status',
    'currentPeriodStart',
    'currentPeriodEnd',
    'billingInterval',
    'cancelAtPeriodEnd',
    'billingEmail',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: ['envId', 'status', ['envId', 'isDeleted']],
}
